/**
 * Provis Coffee — Scroll Reveal System
 * IntersectionObserver-powered scroll animations
 * Supports both class-based (.provis-reveal) and data-attribute (data-pv-reveal) systems
 */

(function() {
  'use strict';

  // Mark page as JS-loaded so CSS animations activate
  document.documentElement.classList.add('pv-js-loaded');

  // Only run if user hasn't opted out of motion
  if (window.matchMedia('(prefers-reduced-motion: reduce)').matches) return;

  /* -------------------------------------------------------
   * 1. CLASS-BASED REVEAL (legacy: .provis-reveal, etc.)
   * ------------------------------------------------------- */
  const classObserver = new IntersectionObserver(
    (entries) => {
      entries.forEach((entry) => {
        if (entry.isIntersecting) {
          entry.target.classList.add('is-visible');
          classObserver.unobserve(entry.target);
        }
      });
    },
    {
      threshold: 0.15,
      rootMargin: '0px 0px -50px 0px'
    }
  );

  /* -------------------------------------------------------
   * 2. DATA-ATTRIBUTE REVEAL (data-pv-reveal)
   * ------------------------------------------------------- */
  const pvObserver = new IntersectionObserver(
    function(entries) {
      entries.forEach(function(e) {
        if (e.isIntersecting) {
          e.target.classList.add('pv-visible');

          // Split-text character stagger
          if (e.target.classList.contains('pv-split-text')) {
            e.target.querySelectorAll('.pv-char').forEach(function(c, i) {
              c.style.transitionDelay = (i * 0.04) + 's';
            });
          }

          // Counter animation
          if (e.target.classList.contains('pv-counter')) {
            animateCounter(e.target);
          }

          pvObserver.unobserve(e.target);
        }
      });
    },
    {
      threshold: 0.15,
      rootMargin: '0px 0px -60px 0px'
    }
  );

  /* -------------------------------------------------------
   * Counter animation helper
   * ------------------------------------------------------- */
  function animateCounter(el) {
    var target = parseInt(el.dataset.pvCount || el.textContent, 10);
    var suffix = el.dataset.pvSuffix || '';
    var prefix = el.dataset.pvPrefix || '';
    var duration = 2000;
    var start = performance.now();

    function update(now) {
      var progress = Math.min((now - start) / duration, 1);
      var eased = 1 - Math.pow(1 - progress, 3);
      el.textContent = prefix + Math.round(eased * target) + suffix;
      if (progress < 1) requestAnimationFrame(update);
    }

    requestAnimationFrame(update);
  }

  /* -------------------------------------------------------
   * Text-splitting for character animations
   * ------------------------------------------------------- */
  function splitText() {
    document.querySelectorAll('[data-pv-split]').forEach(function(el) {
      if (el.dataset.pvSplitDone) return;
      var text = el.textContent;
      var html = '';
      for (var i = 0; i < text.length; i++) {
        html += text[i] === ' ' ? ' ' : '<span class="pv-char">' + text[i] + '</span>';
      }
      el.innerHTML = html;
      el.classList.add('pv-split-text');
      el.dataset.pvSplitDone = '1';
    });
  }

  /* -------------------------------------------------------
   * Parallax on scroll
   *
   * Perf note: batch all layout reads (getBoundingClientRect) before any
   * writes (style.transform). Interleaving reads and writes forces a
   * synchronous layout recalc on every iteration (classic layout thrashing).
   * viewportHeight is cached and only refreshed on resize.
   * ------------------------------------------------------- */
  function initParallax() {
    var els = document.querySelectorAll('[data-pv-parallax]');
    if (!els.length) return;
    var ticking = false;
    var viewportHeight = window.innerHeight;

    window.addEventListener('resize', function() {
      viewportHeight = window.innerHeight;
    }, { passive: true });

    window.addEventListener('scroll', function() {
      if (ticking) return;
      ticking = true;
      requestAnimationFrame(function() {
        // Phase 1: read
        var reads = [];
        for (var i = 0; i < els.length; i++) {
          reads.push({ el: els[i], rect: els[i].getBoundingClientRect() });
        }
        // Phase 2: write
        for (var j = 0; j < reads.length; j++) {
          var speed = parseFloat(reads[j].el.dataset.pvParallax) || 0.15;
          var offset = (reads[j].rect.top + reads[j].rect.height / 2 - viewportHeight / 2) * speed;
          reads[j].el.style.transform = 'translateY(' + offset + 'px)';
        }
        ticking = false;
      });
    }, { passive: true });
  }

  /* -------------------------------------------------------
   * Scroll progress bar — handler removed.
   *
   * The scroll progress bar is owned by snippets/provis-scroll-progress.liquid,
   * which has its own inline script targeting #pv-scroll-progress-fill. The
   * previous handler here was duplicating that work AND targeting the wrong
   * element (the outer .pv-scroll-progress container, with default
   * transform-origin: center, which would have produced an incorrect visual).
   * Single-source-of-truth now lives in the snippet.
   * ------------------------------------------------------- */

  /* -------------------------------------------------------
   * Init: observe all elements
   * ------------------------------------------------------- */
  function initClassReveals() {
    // Legacy class-based sections (skip elements marked with data-pv-no-reveal)
    document.querySelectorAll('.section, .media-with-content, .hero').forEach(function(el) {
      if (el.dataset.pvNoReveal || el.closest('[data-pv-no-reveal]')) return;
      if (!el.classList.contains('provis-reveal')) {
        el.classList.add('provis-reveal');
      }
      classObserver.observe(el);
    });

    // Manually tagged class-based elements
    document.querySelectorAll('.provis-reveal, .provis-reveal-left, .provis-reveal-right, .provis-reveal-stagger').forEach(function(el) {
      classObserver.observe(el);
    });

    // Product cards — no opacity animation, let them be visible immediately
  }

  function initPvReveals() {
    document.querySelectorAll('[data-pv-reveal], .pv-split-text, .pv-counter, .pv-curtain, .pv-gold-reveal').forEach(function(el) {
      pvObserver.observe(el);
    });
  }

  /* -------------------------------------------------------
   * Boot
   * ------------------------------------------------------- */
  function boot() {
    splitText();
    initClassReveals();
    initPvReveals();
    initParallax();
    // initScrollProgress() removed — handled by snippets/provis-scroll-progress.liquid
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', boot);
  } else {
    boot();
  }

  // Re-run on Shopify section rendering (theme editor)
  document.addEventListener('shopify:section:load', boot);
})();
